import { useMutation, useQuery } from '@apollo/client';
import { faEnvelope, faSpinnerThird, faClock } from '@fortawesome/pro-regular-svg-icons';
import * as RadioGroup from '@radix-ui/react-radio-group';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EmailAutomationForm from './EmailAutomationForm.tsx';
import ScheduleForm from './ScheduleForm.tsx';
import {
    ScheduleFormState,
    isFormValid,
    runErrorMessage,
    toFormState,
    toVariables,
    validationErrorMessage,
} from './scheduleFormat.ts';
import { useEmailAutomation } from './useEmailAutomation.ts';
import { useScheduleTestRun } from './useScheduleTestRun.ts';
import Alert from '../../../core/components/Alert.tsx';
import Button from '../../../core/components/Button.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Modal from '../../../core/components/Modal.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { graphqlErrorMessage } from '../../errors.ts';
import {
    CREATE_CONVERSATION,
    CREATE_CONVERSATION_SCHEDULE,
    GET_CHAT_TOOLS,
    GET_CONVERSATION,
    GET_CONVERSATION_SCHEDULES,
    GET_SCHEDULE_DELIVERY_OPTIONS,
    GET_CONVERSATIONS,
    SUGGEST_SCHEDULE_NAME,
    UPDATE_CONVERSATION_SCHEDULE,
} from '../../queries.ts';

const ClockIcon = withIcon(faClock);
const EnvelopeIcon = withIcon(faEnvelope);
const SpinnerIcon = withIcon(faSpinnerThird);

// 'edit' edits a schedule, 'editEmail' an email automation (trigger)
export type AutomationDialogMode = 'new' | 'fromMessage' | 'edit' | 'editEmail';
type AutomationType = 'schedule' | 'email';

interface AutomationDialogProps {
    mode: AutomationDialogMode;
    schedule?: any;
    trigger?: any;
    conversationId?: string;
    initialPrompt?: string;
    // prefill for 'fromMessage' beyond the prompt (a chat schedule proposal's settings)
    initialForm?: Partial<ScheduleFormState>;
    // called once a schedule is created (not on close): resolves the chat proposal this dialog came from
    onSaved?: (schedule: any) => void;
    onClose: () => void;
    // the user may create email automations; otherwise the type step shows the option disabled
    canUseEmail?: boolean;
    emailUnavailableHint?: string;
    // the agents an email automation can run on, and the one to preselect; the projects a personal one may pick
    agents?: { id: string; name: string }[];
    projects?: { id: string; name: string }[];
    initialAgentId?: string | null;
}

interface TypeCardProps {
    value: AutomationType;
    icon: React.ReactNode;
    label: string;
    hint: string;
    isDisabled?: boolean;
}

const TypeCard = ({ value, icon, label, hint, isDisabled = false }: TypeCardProps) => (
    <RadioGroup.Item
        value={value}
        disabled={isDisabled}
        className={classnames(
            'flex cursor-pointer items-start gap-3 rounded-lg border border-solid border-line-weak bg-surface-weak p-4 text-left transition-colors',
            'hover:border-line-strong data-[state=checked]:border-line-brand-default',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong',
            'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[disabled]:hover:border-line-weak'
        )}
    >
        <span className="mt-0.5 flex-none text-content-medium">{icon}</span>
        <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-sm font-medium text-content-strong">{label}</span>
            <span className="text-xs text-content-medium">{hint}</span>
        </span>
    </RadioGroup.Item>
);

const AutomationDialog = ({
    mode,
    schedule,
    trigger,
    conversationId: propsConversationId,
    initialPrompt,
    initialForm,
    onSaved,
    onClose,
    canUseEmail = false,
    emailUnavailableHint,
    agents = [],
    projects = [],
    initialAgentId,
}: AutomationDialogProps) => {
    const { t } = useTranslation('chat');
    const { publishToast } = useToaster();
    const { user } = useApplicationContext();
    const isEdit = mode === 'edit';
    const conversationId = schedule?.conversationId ?? propsConversationId;

    const [step, setStep] = useState<'type' | 'form'>(mode === 'new' ? 'type' : 'form');
    const [type, setType] = useState<AutomationType>('schedule');
    const isEditEmail = mode === 'editEmail';
    const isEmail = isEditEmail || (mode === 'new' && type === 'email');
    const email = useEmailAutomation({
        trigger: isEditEmail ? trigger : undefined,
        initialAgentId,
        agents,
        enabled: isEmail,
    });

    const [form, setForm] = useState<ScheduleFormState>(() => ({
        ...toFormState(isEdit ? schedule : null, {
            prompt: initialPrompt,
            timezone: user?.customer?.timezone,
        }),
        ...(isEdit ? {} : initialForm),
    }));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [testError, setTestError] = useState<string | null>(null);
    const [created, setCreated] = useState<any>(null);
    const createdConversationIdRef = useRef<string | null>(null);

    const { data: conversationData } = useQuery(GET_CONVERSATION, {
        variables: { id: conversationId },
        skip: !conversationId,
    });
    const conversation = conversationData?.agentConversation;

    const { data: toolsData } = useQuery(GET_CHAT_TOOLS, {
        variables: { projectId: conversation?.project?.id ?? null },
        skip: isEditEmail || (Boolean(conversationId) && !conversation),
    });
    const tools = useMemo(
        () => (conversationId && !conversation?.toolsEnabled ? [] : toolsData?.chatTools || []),
        [conversationId, conversation, toolsData]
    );

    const toolsInitializedRef = useRef(isEdit);
    useEffect(() => {
        if (toolsInitializedRef.current || !tools.length) return;
        toolsInitializedRef.current = true;
        const available = tools.map((tool) => tool.name);
        const selected = conversation?.selectedTools
            ? available.filter((name) => conversation.selectedTools.includes(name))
            : available;
        setForm((current) => ({ ...current, selectedTools: selected }));
    }, [tools, conversation]);

    // a name carried over from the proposal card (possibly user-adjusted) must not be replaced by a generated one
    const nameEditedRef = useRef(isEdit || Boolean(initialForm?.name));
    const suggestedForRef = useRef<string | null>(null);
    const [suggestScheduleName, { loading: suggestingName }] = useMutation(SUGGEST_SCHEDULE_NAME);
    const requestNameSuggestion = (text: string) => {
        const trimmed = text.trim();
        if (nameEditedRef.current || !trimmed || suggestedForRef.current === trimmed) return;
        suggestedForRef.current = trimmed;
        const fallBack = () =>
            setForm((current) => (current.name ? current : { ...current, name: conversation?.title || '' }));
        suggestScheduleName({ variables: { prompt: trimmed } })
            .then(({ data }) => {
                if (nameEditedRef.current) return;
                const suggestion = data?.suggestAgentConversationScheduleName?.name;
                if (suggestion) setForm((current) => ({ ...current, name: suggestion }));
                else fallBack();
            })
            .catch(fallBack);
    };

    const initializedRef = useRef(false);
    useEffect(() => {
        if (mode !== 'fromMessage' || initializedRef.current || (conversationId && !conversation)) return;
        initializedRef.current = true;
        if (initialPrompt?.trim()) requestNameSuggestion(initialPrompt);
        else setForm((current) => ({ ...current, name: current.name || conversation?.title || '' }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, conversation]);

    const { data: deliveryData } = useQuery(GET_SCHEDULE_DELIVERY_OPTIONS, { skip: isEditEmail });
    const deliveryOptions = deliveryData?.scheduleDeliveryOptions;
    const mailbox = deliveryOptions
        ? { address: deliveryOptions.mailboxAddress ?? null, connected: Boolean(deliveryOptions.mailboxConnected) }
        : null;

    const handleChange = (patch: Partial<ScheduleFormState>) => {
        if ('name' in patch) nameEditedRef.current = true;
        setForm((current) => ({ ...current, ...patch }));
    };

    const refetch = { refetchQueries: [{ query: GET_CONVERSATION_SCHEDULES }] };
    const [createConversation] = useMutation(CREATE_CONVERSATION, { refetchQueries: [{ query: GET_CONVERSATIONS }] });
    const [createSchedule] = useMutation(CREATE_CONVERSATION_SCHEDULE, {
        refetchQueries: [{ query: GET_CONVERSATION_SCHEDULES }, { query: GET_CONVERSATIONS }],
    });
    const [updateSchedule] = useMutation(UPDATE_CONVERSATION_SCHEDULE, refetch);

    const testRun = useScheduleTestRun();
    const busy = saving || testRun.running;

    useEffect(() => {
        const result = testRun.result;
        if (!result) return;
        testRun.reset();
        if (result.ok) {
            publishToast({ description: t('automations.toasts.savedTested'), status: 'success' });
            onClose();
        } else {
            setTestError(runErrorMessage(t, result.error || 'RUN_FAILED'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testRun.result]);

    const save = async (): Promise<any> => {
        const variables = toVariables(form, mailbox?.address ?? null);
        if (isEdit || created) {
            const target = created ?? schedule;
            // saving without the test resumes a schedule paused after failures, or one created paused for a test
            const resume = !form.testRun && (created ? !created.active : Boolean(schedule.autoPaused));
            const { data } = await updateSchedule({
                variables: { id: target.id, ...variables, ...(resume ? { active: true } : {}) },
            });
            return data?.updateAgentConversationSchedule?.schedule;
        }
        let targetConversationId = conversationId ?? createdConversationIdRef.current;
        if (!targetConversationId) {
            const { data } = await createConversation({
                variables: { toolsEnabled: true, selectedTools: form.selectedTools },
            });
            targetConversationId = data.createAgentConversation.conversation.id;
            createdConversationIdRef.current = targetConversationId;
        }
        const { data } = await createSchedule({
            variables: { conversationId: targetConversationId, ...variables, active: !form.testRun },
        });
        const saved = data?.createAgentConversationSchedule?.schedule;
        setCreated(saved);
        onSaved?.(saved);
        return saved;
    };

    const handleSave = async () => {
        setError(null);
        setTestError(null);
        setSaving(true);
        try {
            const saved = await save();
            if (form.testRun) {
                await testRun.start(saved.id, { resumeOnSuccess: !isEdit || Boolean(schedule?.autoPaused) });
            } else {
                publishToast({ description: t('automations.toasts.saved'), status: 'success' });
                onClose();
            }
        } catch (e) {
            setError(validationErrorMessage(t, e, 'automations.toasts.error'));
        } finally {
            setSaving(false);
        }
    };

    const handleSaveEmail = async () => {
        setError(null);
        setSaving(true);
        try {
            await email.save();
            publishToast({ description: t('trigger.createdToast'), status: 'success' });
            onClose();
        } catch (e) {
            setError(graphqlErrorMessage(e, t('trigger.saveError')));
        } finally {
            setSaving(false);
        }
    };

    const title = t(
        isEditEmail
            ? 'trigger.editLabel'
            : mode === 'edit'
              ? 'automations.dialog.editTitle'
              : mode === 'fromMessage'
                ? 'automations.dialog.fromMessageTitle'
                : 'automations.dialog.newTitle'
    );
    const primaryLabel = t(
        isEmail
            ? isEditEmail
                ? 'trigger.save'
                : 'automations.dialog.create'
            : mode === 'new'
              ? form.testRun
                  ? 'automations.dialog.createAndTest'
                  : 'automations.dialog.create'
              : mode === 'fromMessage'
                ? form.testRun
                    ? 'automations.dialog.scheduleAndTest'
                    : 'automations.dialog.schedule'
                : form.testRun
                  ? 'automations.dialog.saveAndTest'
                  : schedule?.autoPaused
                    ? 'automations.dialog.saveAndResume'
                    : 'automations.dialog.save'
    );

    const buttons =
        step === 'type' ? (
            <>
                <Button variant="outline" onClick={onClose}>
                    {t('automations.dialog.cancel')}
                </Button>
                <Button variant="primary" onClick={() => setStep('form')}>
                    {t('automations.dialog.continue')}
                </Button>
            </>
        ) : (
            <>
                {mode === 'new' && (
                    <Button variant="outline" className="mr-auto" disabled={busy} onClick={() => setStep('type')}>
                        {t('automations.dialog.back')}
                    </Button>
                )}
                {testRun.running ? (
                    // the run goes on without the dialog; its outcome arrives as a run toast
                    <Button variant="outline" onClick={onClose}>
                        {t('automations.dialog.keepInBackground')}
                    </Button>
                ) : (
                    <Button variant="outline" disabled={busy} onClick={onClose}>
                        {t('automations.dialog.cancel')}
                    </Button>
                )}
                <Button
                    variant="primary"
                    disabled={(isEmail ? !email.valid : !isFormValid(form)) || busy}
                    onClick={isEmail ? handleSaveEmail : handleSave}
                >
                    {primaryLabel}
                </Button>
            </>
        );

    return (
        <Modal
            title={title}
            onClose={saving ? undefined : onClose}
            buttons={buttons}
            className="w-full sm:w-144"
            preventCloseOnOverlayClick
        >
            {mode === 'new' && (
                <div
                    className="sticky -top-6 z-10 -mx-6 -mt-6 mb-4 h-1 flex-none overflow-hidden bg-surface-strong"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={step === 'type' ? 50 : 100}
                >
                    <div
                        className={classnames(
                            'h-full bg-surface-brand-strong-default transition-[width] duration-200',
                            step === 'type' ? 'w-1/2' : 'w-full'
                        )}
                    />
                </div>
            )}

            {step === 'type' ? (
                <div className="flex flex-col gap-4 pb-2">
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-content-strong">
                            {t('automations.dialog.typeQuestion')}
                        </span>
                        <span className="text-xs text-content-medium">{t('automations.dialog.typeHint')}</span>
                    </div>
                    <RadioGroup.Root
                        aria-label={t('automations.dialog.typeQuestion')}
                        value={type}
                        onValueChange={(value) => setType(value as AutomationType)}
                        className="grid gap-3 sm:grid-row-2"
                    >
                        <TypeCard
                            icon={<ClockIcon />}
                            label={t('automations.onSchedule')}
                            hint={t('automations.dialog.scheduleHint')}
                            value="schedule"
                        />
                        <TypeCard
                            icon={<EnvelopeIcon />}
                            label={t('automations.onEmail')}
                            hint={(!canUseEmail && emailUnavailableHint) || t('automations.dialog.emailHint')}
                            value="email"
                            isDisabled={!canUseEmail}
                        />
                    </RadioGroup.Root>
                </div>
            ) : (
                <div className="flex flex-col gap-4 pb-2">
                    <div className="flex flex-col gap-1">
                        <h2 className="m-0 text-base font-semibold text-content-strong">
                            {t(isEmail ? 'automations.onEmail' : 'automations.onSchedule')}
                        </h2>
                        <span className="text-sm text-content-medium">
                            {t(isEmail ? 'automations.dialog.emailHint' : 'automations.dialog.scheduleHint')}
                        </span>
                    </div>
                    {testError && (
                        <Alert severity="error">
                            <p className="m-0">
                                <span className="font-medium">{t('automations.dialog.testFailed')}</span> · {testError}
                            </p>
                        </Alert>
                    )}
                    {!testError && isEdit && schedule?.autoPaused && (
                        <Alert severity="error">
                            <p className="m-0">
                                <span className="font-medium">{t('automations.dialog.autoPausedAlert')}</span>
                                {schedule.lastRunError
                                    ? ` · ${t('automations.dialog.lastError', {
                                          error: runErrorMessage(t, schedule.lastRunError),
                                      })}`
                                    : null}
                            </p>
                        </Alert>
                    )}
                    {testRun.running && (
                        <div
                            role="status"
                            className="flex items-center gap-2 rounded-lg bg-surface-medium px-4 py-3 text-sm text-content-strong"
                        >
                            <SpinnerIcon className="flex-none animate-spin text-content-agent-default" />
                            {t('automations.dialog.testing')}
                        </div>
                    )}
                    <fieldset disabled={busy} className="m-0 min-w-0 border-0 p-0">
                        {isEmail ? (
                            <EmailAutomationForm
                                form={email.form}
                                onChange={email.change}
                                agents={agents}
                                projects={projects}
                                channels={email.channels}
                                conditionTypes={email.conditionTypes}
                                isAgentLocked={isEditEmail}
                            />
                        ) : (
                            <ScheduleForm
                                form={form}
                                mailbox={mailbox}
                                onChange={handleChange}
                                isNameGenerating={suggestingName}
                                onPromptBlur={() => !isEdit && requestNameSuggestion(form.prompt)}
                            />
                        )}
                    </fieldset>
                    {error && <Alert severity="error">{error}</Alert>}
                </div>
            )}
        </Modal>
    );
};

export default AutomationDialog;
